/**
 * La ficha de la persona arriba del hilo (§15.3). Compacta en el celular y en la computadora: nombre,
 * número y estado, y «Ver más datos» despliega el resto EN LA PÁGINA. Con la ficha entera arriba, en el
 * celular (y en una notebook de 768 px de alto) el último mensaje y la caja quedaban fuera de la pantalla.
 *
 * El número va completo y visible, sin botón para revelarlo (decisión de Alan, R3). Como la Base registra
 * quién ve un número completo, abrir la conversación también queda registrado, y la ficha lo dice.
 *
 * Las acciones (resolver, baja, CSV, borrar) pasan por `llamar()`: un 422 vuelve resuelto y se pintaría
 * como éxito (R15). Las confirmaciones son tarjetas en la página, nunca un modal (R2).
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Copy, ExternalLink } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle, ExportacionConversacion, FuenteNombre } from "../../types.conversaciones";
import { descargarTexto, llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";
import { ESTADO_CONVERSACION, esComputadora, fechaNegocio } from "../../logic/conversaciones";
import { TarjetaDarDeBaja } from "./TarjetaDarDeBaja";
import { TarjetaSuprimir } from "./TarjetaSuprimir";
import { rutaFicha } from "../../../publico/logic/publico";

const chip = "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap";
const boton = "h-8 px-3 xl:h-7 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white";

const FUENTE_NOMBRE: Record<Exclude<FuenteNombre, "">, string> = {
  base: "(de los contactos con consentimiento)",
  campana: "(de la campaña que recibió)",
  perfil: "(de su perfil de WhatsApp)",
};

type Accion = "resolver" | "sin_responder" | "csv";

function BotonCopiar({ numero }: { numero: string }) {
  const [copiado, setCopiado] = useState(false);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (t.current) clearTimeout(t.current); }, []);
  const copiar = async () => {
    try { await navigator.clipboard?.writeText(numero); } catch { return; }
    setCopiado(true);
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setCopiado(false), 2000);
  };
  return (
    <button type="button" onClick={() => void copiar()} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 underline">
      <Copy size={11} /> {copiado ? "Copiado" : "Copiar"}
    </button>
  );
}

export function FichaConversacion({ conversacion: c, onCambio }: {
  conversacion: ConversacionDetalle;
  /** Recarga el hilo y avisa el cambio (la lista y el badge). */
  onCambio: () => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const [tarjeta, setTarjeta] = useState<"baja" | "suprimir" | null>(null);
  const [enCurso, setEnCurso] = useState<Accion | null>(null);
  const [errores, setErrores] = useState<Partial<Record<Accion, string>>>({});
  const tarjetaRef = useRef<HTMLDivElement>(null);

  // Desde `xl` la ficha tiene su propio scroll (la caja no se mueve de abajo): la tarjeta que se abre quedaba con sus
  // botones fuera de la vista (a 1440x900, 103 px de «Borrar los datos» abajo del borde). Se lleva lo justo, moviendo sólo
  // la ficha. En el celular no: ahí se mueve la página, y la caja fija taparía lo que se lleve al borde de abajo.
  useEffect(() => {
    if (tarjeta && esComputadora()) tarjetaRef.current?.scrollIntoView({ block: "nearest" });
  }, [tarjeta]);

  const estado = ESTADO_CONVERSACION[c.estado] ?? { texto: String(c.estado || "sin estado"), cls: "bg-slate-100 text-slate-500" };
  const conNumero = !!c.numero;
  const titulo = c.nombre || c.numero || "Usuario de WhatsApp";
  const tituloEsNumero = !c.nombre && conNumero;
  const enBaja = c.baja.enNoContactar || c.estado === "baja";
  const sinPermiso = !c.puedeContestar;

  const ejecutar = async (accion: Accion) => {
    if (enCurso) return;
    setEnCurso(accion);
    setErrores((e) => ({ ...e, [accion]: "" }));
    if (accion === "csv") {
      const l = await llamar<ExportacionConversacion>(() => conversacionesApi.exportar(c.id));
      setEnCurso(null);
      if (!l.ok || !l.data) { setErrores((e) => ({ ...e, csv: l.ok ? "El servidor no devolvió el archivo." : textoError(l) })); return; }
      descargarTexto(l.data.csv, l.data.nombreArchivo || `conversacion-${c.id}.csv`);
      return;
    }
    const l = await llamar<ConversacionDetalle>(() => conversacionesApi.cambiarEstado(c.id, accion === "resolver" ? "resuelta" : "sin_responder"));
    setEnCurso(null);
    if (!l.ok) { setErrores((e) => ({ ...e, [accion]: textoError(l) })); return; }
    onCambio();
  };

  const errorDe = (a: Accion) => (errores[a] ? <span role="alert" className="text-xs text-red-700">{errores[a]}</span> : null);

  return (
    // Desde `xl` va arriba del hilo en una columna del alto de la pantalla: más apretada, y el número al lado del nombre
    // (en su propio renglón eran 21 px menos de hilo). En el celular el número sigue bajando solo, por el `flex-wrap`.
    <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm space-y-3 xl:space-y-2 xl:p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <h2 className={`text-base font-semibold text-slate-900 ${tituloEsNumero ? "font-mono whitespace-nowrap" : ""}`}>{titulo}</h2>
            {tituloEsNumero && <BotonCopiar numero={c.numero} />}
            <span className={`${chip} ${estado.cls}`}>{estado.texto}</span>
            {c.nombre && conNumero && (
              <p className="text-sm text-slate-700">
                Número: <span className="font-mono whitespace-nowrap">{c.numero}</span> <BotonCopiar numero={c.numero} />
              </p>
            )}
          </div>
          {!conNumero && !c.anonimizada && <p className="text-sm text-slate-700">Escribió sin número de teléfono. Usuario de WhatsApp: {c.usuario || "sin dato"}</p>}
          <p className="text-[11px] text-slate-400">Queda registrado que abriste esta conversación.</p>
        </div>
        <button type="button" onClick={() => setAbierta((x) => !x)} aria-expanded={abierta}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900">
          {abierta ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Ver más datos
        </button>
      </div>

      {c.otroNumero && (
        <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Te escribió al <span className="whitespace-nowrap">{c.otroNumero.recibio}</span>, que ya no es el número de la marca
          (<span className="whitespace-nowrap">{c.otroNumero.actual}</span>). Desde el número actual sólo se le puede escribir con una plantilla.
        </div>
      )}

      {enBaja && (
        <div className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 space-y-1">
          <p>Pidió no recibir mensajes{c.baja.desde ? ` (desde el ${fechaNegocio(c.baja.desde)})` : ""}. No se le mandan campañas ni plantillas.</p>
          {c.baja.puedeContestar && <p>Como volvió a escribir después, se le puede contestar con texto libre dentro de las 24 h.</p>}
          <Link to="/activa/oficial/bajas" className="font-semibold underline">Ver Bajas y consentimientos</Link>
        </div>
      )}

      {c.anonimizada && (
        <div className="rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600">
          Los datos de esta conversación se borraron (supresión o vencimiento de los 12 meses).
        </div>
      )}

      {abierta && (
        <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700 space-y-1.5">
          {c.nombre && c.fuenteNombre && (
            <p>Nombre: {c.nombre} <span className="text-xs text-slate-500">{FUENTE_NOMBRE[c.fuenteNombre]}</span></p>
          )}
          {c.nombrePerfil && c.nombrePerfil !== c.nombre && <p>Nombre en su perfil de WhatsApp: {c.nombrePerfil}</p>}
          {c.contactoId && (
            <p><Link to={rutaFicha(`b:${c.contactoId}`)} className="font-semibold text-violet-700 underline">Ver su ficha en Público</Link></p>
          )}
          {c.campanaOrigen ? (
            <p>
              Llegó por la campaña{" "}
              <Link to={`/activa/oficial/campanas/${encodeURIComponent(c.campanaOrigen.id)}`} className="font-semibold text-violet-700 underline">«{c.campanaOrigen.nombre}»</Link>
            </p>
          ) : c.referral ? (
            <p>
              Llegó por un anuncio ({c.referral.sourceType || "sin tipo"})
              {c.referral.sourceUrl && (
                <> · <a href={c.referral.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-violet-700 underline">Ver anuncio <ExternalLink size={11} /></a></>
              )}
            </p>
          ) : (
            <p>Escribió sin que le mandáramos una campaña</p>
          )}
        </div>
      )}

      {!c.anonimizada && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {(c.estado === "sin_responder" || c.estado === "respondida") && (
              <span className="inline-flex flex-wrap items-center gap-2">
                <button type="button" className={boton} disabled={sinPermiso || !!enCurso} onClick={() => void ejecutar("resolver")}>Marcar resuelta</button>
                {errorDe("resolver")}
              </span>
            )}
            {(c.estado === "resuelta" || c.estado === "respondida") && (
              <span className="inline-flex flex-wrap items-center gap-2">
                <button type="button" className={boton} disabled={sinPermiso || !!enCurso} onClick={() => void ejecutar("sin_responder")}>Marcar sin responder</button>
                {errorDe("sin_responder")}
              </span>
            )}
            <span className="inline-flex flex-wrap items-center gap-2">
              <button type="button" className={boton} disabled={sinPermiso || !!enCurso} onClick={() => void ejecutar("csv")}>Descargar la conversación (CSV)</button>
              {errorDe("csv")}
            </span>
            {/* Con `bajaPosible` y no con el número: quien escribió con su usuario citando una campaña no tiene `numero`, y el
                backend igual le da la baja con el número al que salió esa campaña. Atado al número, lo único que le
                quedaba al operador era «Borrar los datos», que borra lo que la persona no pidió borrar. */}
            {c.bajaPosible && !enBaja && (
              <button type="button" className={boton} disabled={sinPermiso} onClick={() => setTarjeta("baja")} aria-expanded={tarjeta === "baja"}>Dar de baja</button>
            )}
            {c.puedeSuprimir && (
              <button type="button" className={`${boton} text-red-700`} onClick={() => setTarjeta("suprimir")} aria-expanded={tarjeta === "suprimir"}>
                Borrar los datos de esta persona
              </button>
            )}
          </div>
          {sinPermiso && c.motivoNoPuedeContestar && <p className="text-xs text-slate-500">{c.motivoNoPuedeContestar}</p>}
          {tarjeta && (
            <div ref={tarjetaRef} data-testid="tarjeta-ficha">
              {tarjeta === "baja" && <TarjetaDarDeBaja conversacionId={c.id} onCancelar={() => setTarjeta(null)} onListo={onCambio} />}
              {tarjeta === "suprimir" && c.puedeSuprimir && (
                <TarjetaSuprimir conversacion={c} onCancelar={() => setTarjeta(null)} onListo={() => { setTarjeta(null); onCambio(); }} />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default FichaConversacion;
